import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

export default function Datenschutz() {
  const { lang } = useLanguage();
  const isDe = lang === "de";

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="datenschutz-page">
      <header className="bg-[#0F172A] text-white py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 text-sm font-semibold tracking-wider uppercase mb-6 transition-colors"
            data-testid="datenschutz-back-link"
          >
            <ArrowLeft size={16} />
            {isDe ? "Zurück zur Startseite" : "Back to home"}
          </Link>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
            {isDe ? "Datenschutzerklärung" : "Privacy Policy"}
          </h1>
          <p className="mt-4 text-base sm:text-lg text-slate-300 max-w-2xl">
            {isDe
              ? "Informationen zur Verarbeitung Ihrer personenbezogenen Daten gemäß DSGVO."
              : "Information on the processing of your personal data according to the GDPR."}
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 lg:px-8 py-16 lg:py-24 space-y-10">
        <section>
          <h2 className="font-display text-xl font-bold text-cyan-600 uppercase tracking-[0.2em] text-[11px] mb-4">
            {isDe ? "1. Verantwortliche Stelle" : "1. Controller"}
          </h2>
          <p className="text-base text-slate-700 leading-relaxed">
            {isDe
              ? "Verantwortlich für die Datenverarbeitung auf dieser Website ist ZEMKA Gebäudereinigung & Hausmeister Service, Inh. Zemrije Ademi, Sigmundstraße, 82377 Penzberg. Kontakt: 0170 8251779, zemka.gebaeudereinigung@gmail.com."
              : "The party responsible for data processing on this website is ZEMKA Gebäudereinigung & Hausmeister Service, owner Zemrije Ademi, Sigmundstraße, 82377 Penzberg, Germany. Contact: +49 170 8251779, zemka.gebaeudereinigung@gmail.com."}
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-bold text-cyan-600 uppercase tracking-[0.2em] text-[11px] mb-4">
            {isDe ? "2. Erhebung und Verarbeitung von Daten" : "2. Collection and processing of data"}
          </h2>
          <p className="text-base text-slate-700 leading-relaxed">
            {isDe
              ? "Wenn Sie unser Kontaktformular ausfüllen, verarbeiten wir Ihren Namen, Ihre E-Mail-Adresse, optional Ihre Telefonnummer sowie Ihre Nachricht ausschließlich zur Bearbeitung Ihrer Anfrage. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO (vorvertragliche Maßnahmen)."
              : "When you fill out our contact form, we process your name, email address, optionally your phone number and your message exclusively to handle your inquiry. The legal basis is Art. 6 (1) (b) GDPR (pre-contractual measures)."}
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-bold text-cyan-600 uppercase tracking-[0.2em] text-[11px] mb-4">
            {isDe ? "3. Speicherdauer" : "3. Storage period"}
          </h2>
          <p className="text-base text-slate-700 leading-relaxed">
            {isDe
              ? "Ihre Anfragedaten werden gelöscht, sobald sie zur Erreichung des Zwecks ihrer Erhebung nicht mehr erforderlich sind. Gesetzliche Aufbewahrungsfristen bleiben unberührt."
              : "Your inquiry data is deleted as soon as it is no longer required to achieve the purpose of its collection. Statutory retention periods remain unaffected."}
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-bold text-cyan-600 uppercase tracking-[0.2em] text-[11px] mb-4">
            {isDe ? "4. Ihre Rechte" : "4. Your rights"}
          </h2>
          <p className="text-base text-slate-700 leading-relaxed">
            {isDe
              ? "Sie haben das Recht auf Auskunft (Art. 15 DSGVO), Berichtigung (Art. 16), Löschung (Art. 17), Einschränkung der Verarbeitung (Art. 18), Datenübertragbarkeit (Art. 20) sowie Widerspruch (Art. 21). Außerdem können Sie sich bei der zuständigen Aufsichtsbehörde beschweren."
              : "You have the right to access (Art. 15 GDPR), rectification (Art. 16), erasure (Art. 17), restriction of processing (Art. 18), data portability (Art. 20) and objection (Art. 21). You may also lodge a complaint with the competent supervisory authority."}
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-bold text-cyan-600 uppercase tracking-[0.2em] text-[11px] mb-4">
            {isDe ? "5. SSL-Verschlüsselung" : "5. SSL encryption"}
          </h2>
          <p className="text-base text-slate-700 leading-relaxed">
            {isDe
              ? 'Diese Seite nutzt aus Sicherheitsgründen eine SSL/TLS-Verschlüsselung. Eine verschlüsselte Verbindung erkennen Sie am „https://" in der Adresszeile.'
              : 'This site uses SSL/TLS encryption for security reasons. You can recognise an encrypted connection by the "https://" in your browser\'s address bar.'}
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-bold text-cyan-600 uppercase tracking-[0.2em] text-[11px] mb-4">
            {isDe ? "6. Cookies" : "6. Cookies"}
          </h2>
          <p className="text-base text-slate-700 leading-relaxed">
            {isDe
              ? "Diese Website setzt aktuell keine Tracking-Cookies oder Analyse-Dienste ein. Lediglich technisch notwendige Speicher­funktionen (z. B. Sprachauswahl im lokalen Browser-Speicher) werden genutzt."
              : "This website currently does not use any tracking cookies or analytics services. Only technically necessary storage (e.g. language selection in local browser storage) is used."}
          </p>
        </section>
      </main>
    </div>
  );
}
